import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import Navigation from '../components/Navigation';
import Logo from '../components/Logo';
import { ListFooter } from '../ui';
import { reportError } from '../utils/reportError';

const DAYS = ['MON','TUE','WED','THU','FRI','SAT'];
// Line-level downtime reasons come from the downtime_reasons master (no free text).
const fmtTime = (ts) => (ts ? new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '');
// CentreBoard is LINE scope → offer the line-relevant reason categories only.
const LINE_REASON_CATS = ['material', 'manpower', 'power', 'planned'];

const getWeekStart = (offset=0) => {
  const d = new Date();
  d.setDate(d.getDate() - d.getDay() + 1 + offset*7);
  return d.toISOString().split('T')[0];
};
const getWeekDates = (ws) => {
  const dates = [];
  for(let i=0;i<6;i++){const d=new Date(ws);d.setDate(d.getDate()+i);dates.push(d.toISOString().split('T')[0]);}
  return dates;
};
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'short'}) : '';
const effColor = (e) => !e?'#888':e>=100?'#22c55e':e>=85?'#f59e0b':'#ef4444';
const effBg = (e) => !e?'#f3f4f6':e>=100?'#dcfce7':e>=85?'#fef9c3':'#fee2e2';

export function CentreBoardContent() {
  const [view, setView] = useState('board');
  const [weekOffset, setWeekOffset] = useState(0);
  const [weekStart, setWeekStart] = useState(getWeekStart(0));
  const [boardData, setBoardData] = useState({ lines:[], day_summary:[], week_summary:{}, week_dates:[] });
  const [downtime, setDowntime] = useState([]);
  const [monthly, setMonthly] = useState({ cutting:[], stitching:[], finishing:[], weeks:[] });
  const [quarterly, setQuarterly] = useState({ buyer_wise:[], capacity_wise:[], monthly_summary:[] });
  const [showDowntimeModal, setShowDowntimeModal] = useState(false);
  const [dtForm, setDtForm] = useState({ dept:'cutting', line_id:'', reason_id:'', start_time:'', stop_time:'', remarks:'' });
  const [dtLines, setDtLines] = useState([]);
  const [dtReasons, setDtReasons] = useState([]);
  const [saving, setSaving] = useState(false);
  const [month, setMonth] = useState(new Date().getMonth()+1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [quarter, setQuarter] = useState(Math.ceil((new Date().getMonth()+1)/3));
  const autoRef = useRef();

  useEffect(() => {
    const ws = getWeekStart(weekOffset);
    setWeekStart(ws);
    fetchBoard(ws);
    fetchDowntime(ws);
  }, [weekOffset]);

  useEffect(() => {
    api.get('/ppc/lines').then(r => setDtLines(r.data.data || [])).catch(() => {});
    api.get('/maintenance/downtime-reasons').then(r => setDtReasons(r.data.data || [])).catch(() => {});
  }, []);

  useEffect(() => { if(view==='monthly') fetchMonthly(); }, [view, month, year]);
  useEffect(() => { if(view==='quarterly') fetchQuarterly(); }, [view, quarter, year]);

  useEffect(() => {
    if(view==='display') {
      autoRef.current = setInterval(() => fetchBoard(weekStart), 300000);
      return () => clearInterval(autoRef.current);
    }
  }, [view, weekStart]);

  const fetchBoard = async (ws) => {
    try {
      const r = await api.get('/display/centre-board?week_start='+ws);
      setBoardData(r.data);
    } catch(e) { console.error(e); }
  };

  const fetchDowntime = async (ws) => {
    try {
      const dates = getWeekDates(ws);
      const r = await api.get('/planning/downtime?date='+dates[0]);
      setDowntime(r.data.data||[]);
    } catch(e) {}
  };

  const fetchMonthly = async () => {
    try {
      const r = await api.get('/display/monthly?month='+month+'&year='+year);
      setMonthly(r.data);
    } catch(e) {}
  };

  const fetchQuarterly = async () => {
    try {
      const r = await api.get('/display/quarterly?quarter='+quarter+'&year='+year);
      setQuarterly(r.data);
    } catch(e) {}
  };

  const saveDowntime = async (e) => {
    e.preventDefault();
    if (!dtForm.line_id || !dtForm.reason_id || !dtForm.start_time || !dtForm.stop_time) { alert('Line, reason, start and stop are required.'); return; }
    setSaving(true);
    try {
      const day = new Date().toISOString().split('T')[0];
      await api.post('/planning/downtime', {
        log_date: day, dept: dtForm.dept, line_id: dtForm.line_id, reason_id: dtForm.reason_id,
        start_at: `${day}T${dtForm.start_time}:00`, end_at: `${day}T${dtForm.stop_time}:00`, remarks: dtForm.remarks || null,
      });
      setShowDowntimeModal(false);
      setDtForm({ dept:'cutting', line_id:'', reason_id:'', start_time:'', stop_time:'', remarks:'' });
      fetchDowntime(weekStart);
    } catch(err) { alert('Error: '+(err.response?.data?.error||err.message)); }
    finally { setSaving(false); }
  };

  const weekDates = getWeekDates(weekStart);
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  const getLineData = (lineNo) => weekDates.map(d => boardData.lines.find(l => l.line_no===lineNo && l.plan_date && l.plan_date.startsWith(d)));
  const getDaySummary = (d) => boardData.day_summary.find(s => s.plan_date && s.plan_date.startsWith(d));
  // line_no now comes from lines.line_no ('Line 3'); board grid uses the numeric — match on digits.
  const getLineDowntime = (lineNo, d) => downtime.filter(dt => String(dt.line_no||'').replace(/\D/g,'')===String(lineNo) && dt.log_date && dt.log_date.startsWith(d));

  const inputStyle = {width:'100%',padding:'7px 10px',border:'1px solid #ddd',borderRadius:6,fontSize:13,boxSizing:'border-box'};
  const labelStyle = {fontSize:11,color:'#888',fontWeight:600,display:'block',marginBottom:4,textTransform:'uppercase'};

  // ─── DISPLAY VIEW (TV/Print) ──────────────────────────────────────────────
  if(view==='display') return (
    <div style={{minHeight:'100vh',background:'#0f172a',color:'white',fontFamily:'inherit',padding:20}}>
      <style>{`@media print { .no-print{display:none!important} body{background:white!important} * {color:black!important} }`}</style>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
        <div>
          <div style={{fontSize:22,fontWeight:900,color:'#E22D2D',letterSpacing:1}}>RSR INTERNATIONAL</div>
          <div style={{fontSize:13,color:'#94a3b8'}}>WEEKLY CUTTING CAPACITY BOOKING & ACTION PLAN</div>
          <div style={{fontSize:12,color:'#64748b'}}>Week: {fmtDate(weekStart)} — {fmtDate(weekDates[5])}</div>
        </div>
        <div className="no-print" style={{display:'flex',gap:8}}>
          <button onClick={() => fetchBoard(weekStart)} style={{padding:'6px 14px',background:'#1e293b',color:'white',border:'1px solid #334155',borderRadius:6,cursor:'pointer',fontSize:12}}>🔄 Refresh</button>
          <button onClick={() => window.print()} style={{padding:'6px 14px',background:'#3B3A8F',color:'white',border:'none',borderRadius:6,cursor:'pointer',fontSize:12}}>🖨️ Print</button>
          <button onClick={() => setView('board')} style={{padding:'6px 14px',background:'#334155',color:'white',border:'none',borderRadius:6,cursor:'pointer',fontSize:12}}>← Back</button>
        </div>
      </div>
      <div style={{overflowX:'auto'}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead>
            <tr style={{background:'#1e293b'}}>
              <th style={{padding:'10px 12px',color:'#94a3b8',textAlign:'left',border:'1px solid #334155',minWidth:60}}>LINE</th>
              <th style={{padding:'10px 12px',color:'#94a3b8',textAlign:'left',border:'1px solid #334155',minWidth:120}}>DETAILS</th>
              {weekDates.map((d,i) => (
                <th key={d} colSpan={3} style={{padding:'10px 12px',color:'#94a3b8',textAlign:'center',border:'1px solid #334155',minWidth:180}}>
                  {DAYS[i]} — {fmtDate(d)}
                </th>
              ))}
            </tr>
            <tr style={{background:'#0f172a'}}>
              <th style={{border:'1px solid #334155'}}></th>
              <th style={{border:'1px solid #334155'}}></th>
              {weekDates.map(d => (
                <React.Fragment key={d}>
                  <th style={{padding:'6px 8px',color:'#60a5fa',fontSize:11,border:'1px solid #334155',textAlign:'center'}}>PLAN</th>
                  <th style={{padding:'6px 8px',color:'#4ade80',fontSize:11,border:'1px solid #334155',textAlign:'center'}}>ACTUAL</th>
                  <th style={{padding:'6px 8px',color:'#f59e0b',fontSize:11,border:'1px solid #334155',textAlign:'center'}}>EFF%</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {[1,2,3,4,5,6,7].map(line => {
              const cells = getLineData(line);
              return (
                <tr key={line} style={{borderBottom:'1px solid #1e293b'}}>
                  <td style={{padding:'10px 12px',fontWeight:900,color:'#3B3A8F',fontSize:16,textAlign:'center',background:'#1e293b',border:'1px solid #334155'}}>{line}</td>
                  <td style={{padding:'8px 10px',border:'1px solid #334155'}}>
                    {cells.filter(Boolean).map((c,i) => c && <div key={i} style={{fontSize:10,color:'#94a3b8',marginBottom:2}}>{c.gpo_no} {c.buyer}</div>)}
                  </td>
                  {weekDates.map((d,di) => {
                    const c = cells[di];
                    const eff = c&&c.target_qty>0?Math.round(c.actual_qty/c.target_qty*100):null;
                    const dt = getLineDowntime(line, d);
                    const dtMins = dt.reduce((s,x)=>s+(x.duration_mins||0),0);
                    return (
                      <React.Fragment key={d}>
                        <td style={{padding:'8px 10px',textAlign:'center',border:'1px solid #334155',color:'#60a5fa',fontWeight:700}}>
                          {c?Number(c.target_qty).toLocaleString('en-IN'):'—'}
                          {dtMins>0&&<div style={{fontSize:9,color:'#ef4444'}}>⚠️ DT:{dtMins}m</div>}
                        </td>
                        <td style={{padding:'8px 10px',textAlign:'center',border:'1px solid #334155',color:c&&c.actual_qty>0?'#4ade80':'#475569',fontWeight:700}}>
                          {c&&c.actual_qty>0?Number(c.actual_qty).toLocaleString('en-IN'):'—'}
                        </td>
                        <td style={{padding:'8px 10px',textAlign:'center',border:'1px solid #334155',fontWeight:700,color:effColor(eff),background:eff?effBg(eff)+'33':'transparent'}}>
                          {eff?eff+'%':'—'}
                        </td>
                      </React.Fragment>
                    );
                  })}
                </tr>
              );
            })}
            <tr style={{background:'#1e293b',borderTop:'2px solid #3B3A8F'}}>
              <td colSpan={2} style={{padding:'10px 12px',fontWeight:700,color:'white',border:'1px solid #334155'}}>DAY TOTAL</td>
              {weekDates.map(d => {
                const s = getDaySummary(d);
                const eff = s&&s.day_target>0?Math.round(s.day_actual/s.day_target*100):null;
                return (
                  <React.Fragment key={d}>
                    <td style={{padding:'8px 10px',textAlign:'center',border:'1px solid #334155',color:'#60a5fa',fontWeight:700}}>{s?Number(s.day_target).toLocaleString('en-IN'):'—'}</td>
                    <td style={{padding:'8px 10px',textAlign:'center',border:'1px solid #334155',color:'#4ade80',fontWeight:700}}>{s?Number(s.day_actual).toLocaleString('en-IN'):'—'}</td>
                    <td style={{padding:'8px 10px',textAlign:'center',border:'1px solid #334155',fontWeight:900,fontSize:14,color:effColor(eff)}}>{eff?eff+'%':'—'}</td>
                  </React.Fragment>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
      {boardData.week_summary && (
        <div style={{display:'flex',gap:20,marginTop:16,padding:'12px 16px',background:'#1e293b',borderRadius:8}}>
          <span style={{color:'#94a3b8'}}>WEEK TARGET: <b style={{color:'#60a5fa'}}>{Number(boardData.week_summary.week_target||0).toLocaleString('en-IN')}</b></span>
          <span style={{color:'#94a3b8'}}>WEEK ACTUAL: <b style={{color:'#4ade80'}}>{Number(boardData.week_summary.week_actual||0).toLocaleString('en-IN')}</b></span>
          <span style={{color:'#94a3b8'}}>WEEK EFFICIENCY: <b style={{color:effColor(boardData.week_summary.week_efficiency)}}>{boardData.week_summary.week_efficiency||0}%</b></span>
          <span style={{color:'#64748b',fontSize:11,marginLeft:'auto'}}>Auto-refreshes every 5 min</span>
        </div>
      )}
    </div>
  );

  // ─── MAIN VIEW ────────────────────────────────────────────────────────────
  return (
    <>
      <div style={{padding:24}}>
        <div style={{background:'white',borderRadius:10,boxShadow:'none',border:'1px solid #E8E7E7'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'0 20px',borderBottom:'1px solid #eee'}}>
            <div style={{display:'flex'}}>
              {[{key:'board',label:'📋 Centre Board'},{key:'monthly',label:'📅 Monthly Plan'},{key:'quarterly',label:'📊 Quarterly Plan'},{key:'downtime',label:'⚠️ Downtime Log'}].map(t => (
                <button key={t.key} onClick={() => setView(t.key)} style={{padding:'14px 18px',border:'none',background:'transparent',cursor:'pointer',fontSize:13,fontWeight:view===t.key?600:400,color:view===t.key?'#3B3A8F':'#888',borderBottom:view===t.key?'2px solid #3B3A8F':'2px solid transparent'}}>{t.label}</button>
              ))}
            </div>
            <div style={{display:'flex',gap:8}}>
              {view==='board' && <>
                <button onClick={() => setWeekOffset(w=>w-1)} style={{padding:'5px 12px',background:'#f0f2f8',border:'1px solid #ddd',borderRadius:5,cursor:'pointer',fontSize:12}}>← Prev</button>
                <span style={{fontSize:13,fontWeight:600,color:'#3B3A8F',padding:'5px 8px'}}>Week: {fmtDate(weekStart)} — {fmtDate(weekDates[5])}</span>
                <button onClick={() => setWeekOffset(w=>w+1)} style={{padding:'5px 12px',background:'#f0f2f8',border:'1px solid #ddd',borderRadius:5,cursor:'pointer',fontSize:12}}>Next →</button>
                <button onClick={() => setView('display')} style={{padding:'5px 14px',background:'#3B3A8F',color:'white',border:'none',borderRadius:5,cursor:'pointer',fontSize:12}}>🖥️ Display Mode</button>
                <button onClick={() => window.print()} style={{padding:'5px 14px',background:'#E22D2D',color:'white',border:'none',borderRadius:5,cursor:'pointer',fontSize:12}}>🖨️ Print</button>
              </>}
              {view==='monthly' && <>
                <select value={month} onChange={e=>setMonth(Number(e.target.value))} style={{padding:'5px 10px',border:'1px solid #ddd',borderRadius:5,fontSize:12}}>
                  {MONTHS.map((m,i) => <option key={i} value={i+1}>{m}</option>)}
                </select>
                <select value={year} onChange={e=>setYear(Number(e.target.value))} style={{padding:'5px 10px',border:'1px solid #ddd',borderRadius:5,fontSize:12}}>
                  {[2024,2025,2026].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </>}
              {view==='quarterly' && <>
                <select value={quarter} onChange={e=>setQuarter(Number(e.target.value))} style={{padding:'5px 10px',border:'1px solid #ddd',borderRadius:5,fontSize:12}}>
                  <option value={1}>Q1 (Jan-Mar)</option><option value={2}>Q2 (Apr-Jun)</option>
                  <option value={3}>Q3 (Jul-Sep)</option><option value={4}>Q4 (Oct-Dec)</option>
                </select>
                <select value={year} onChange={e=>setYear(Number(e.target.value))} style={{padding:'5px 10px',border:'1px solid #ddd',borderRadius:5,fontSize:12}}>
                  {[2024,2025,2026].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </>}
              {view==='downtime' && <button onClick={() => setShowDowntimeModal(true)} style={{padding:'6px 14px',background:'#E22D2D',color:'white',border:'none',borderRadius:6,fontSize:13,cursor:'pointer',fontWeight:600}}>+ Log Downtime</button>}
            </div>
          </div>

          <div style={{padding:20}}>
            {view==='board' && (
              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                  <thead>
                    <tr style={{background:'#3B3A8F'}}>
                      <th style={{padding:'8px 12px',color:'white',textAlign:'left',minWidth:60}}>LINE</th>
                      <th style={{padding:'8px 12px',color:'white',textAlign:'left',minWidth:140}}>DETAILS</th>
                      {weekDates.map((d,i) => (
                        <th key={d} colSpan={3} style={{padding:'8px 12px',color:'white',textAlign:'center',minWidth:180}}>
                          {DAYS[i]}<br/><span style={{fontSize:10,opacity:0.8}}>{fmtDate(d)}</span>
                        </th>
                      ))}
                    </tr>
                    <tr style={{background:'#EEEDF8'}}>
                      <th style={{border:'1px solid #ddd'}}></th><th style={{border:'1px solid #ddd'}}></th>
                      {weekDates.map(d => (
                        <React.Fragment key={d}>
                          <th style={{padding:'5px 8px',color:'#3B3A8F',fontSize:10,border:'1px solid #ddd',textAlign:'center'}}>PLAN</th>
                          <th style={{padding:'5px 8px',color:'#166534',fontSize:10,border:'1px solid #ddd',textAlign:'center'}}>ACTUAL</th>
                          <th style={{padding:'5px 8px',color:'#9A6B00',fontSize:10,border:'1px solid #ddd',textAlign:'center'}}>EFF%</th>
                        </React.Fragment>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[1,2,3,4,5,6,7].map(line => {
                      const cells = getLineData(line);
                      return (
                        <tr key={line} style={{borderBottom:'1px solid #f0f0f0'}}>
                          <td style={{padding:'10px 12px',fontWeight:900,color:'#3B3A8F',fontSize:16,textAlign:'center',background:'#EEEDF8',border:'1px solid #ddd'}}>{line}</td>
                          <td style={{padding:'8px 10px',border:'1px solid #eee',fontSize:11}}>
                            {cells.filter(Boolean).slice(0,1).map((c,i) => c&&<div key={i}><div style={{fontWeight:600,color:'#333'}}>{c.gpo_no}</div><div style={{color:'#888'}}>{c.buyer}</div><div style={{color:'#aaa',fontSize:10}}>{c.fabric_type}</div></div>)}
                          </td>
                          {weekDates.map((d,di) => {
                            const c = cells[di];
                            const eff = c&&c.target_qty>0?Math.round(c.actual_qty/c.target_qty*100):null;
                            const dt = getLineDowntime(line,d);
                            const dtMins = dt.reduce((s,x)=>s+(x.duration_mins||0),0);
                            return (
                              <React.Fragment key={d}>
                                <td style={{padding:'8px 10px',textAlign:'center',border:'1px solid #eee',fontWeight:700,color:'#3B3A8F'}}>
                                  {c&&c.target_qty>0?Number(c.target_qty).toLocaleString('en-IN'):'—'}
                                  {dtMins>0&&<div style={{fontSize:9,color:'#ef4444'}}>⚠️ {dtMins}m DT</div>}
                                </td>
                                <td style={{padding:'8px 10px',textAlign:'center',border:'1px solid #eee',fontWeight:700,color:c&&c.actual_qty>0?'#166534':'#aaa'}}>
                                  {c&&c.actual_qty>0?Number(c.actual_qty).toLocaleString('en-IN'):'—'}
                                </td>
                                <td style={{padding:'8px 10px',textAlign:'center',border:'1px solid #eee',fontWeight:700,background:eff?effBg(eff):'transparent',color:effColor(eff)}}>
                                  {eff?eff+'%':'—'}
                                </td>
                              </React.Fragment>
                            );
                          })}
                        </tr>
                      );
                    })}
                    <tr style={{background:'#f0f2f8',borderTop:'2px solid #3B3A8F'}}>
                      <td colSpan={2} style={{padding:'8px 12px',fontWeight:700}}>DAY TOTAL</td>
                      {weekDates.map(d => {
                        const s = getDaySummary(d);
                        const eff = s&&s.day_target>0?Math.round(s.day_actual/s.day_target*100):null;
                        return (
                          <React.Fragment key={d}>
                            <td style={{padding:'8px 10px',textAlign:'center',fontWeight:700,color:'#3B3A8F'}}>{s?Number(s.day_target).toLocaleString('en-IN'):'—'}</td>
                            <td style={{padding:'8px 10px',textAlign:'center',fontWeight:700,color:'#166534'}}>{s?Number(s.day_actual).toLocaleString('en-IN'):'—'}</td>
                            <td style={{padding:'8px 10px',textAlign:'center',fontWeight:900,color:effColor(eff)}}>{eff?eff+'%':'—'}</td>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
                {boardData.week_summary && (
                  <div style={{display:'flex',gap:24,marginTop:12,padding:'10px 16px',background:'#EEEDF8',borderRadius:6,fontSize:13}}>
                    <span>Week Target: <b style={{color:'#3B3A8F'}}>{Number(boardData.week_summary.week_target||0).toLocaleString('en-IN')}</b></span>
                    <span>Week Actual: <b style={{color:'#166534'}}>{Number(boardData.week_summary.week_actual||0).toLocaleString('en-IN')}</b></span>
                    <span>Week Efficiency: <b style={{color:effColor(boardData.week_summary.week_efficiency)}}>{boardData.week_summary.week_efficiency||0}%</b></span>
                  </div>
                )}
              </div>
            )}

            {view==='monthly' && (
              <div>
                <div style={{marginBottom:16,fontWeight:700,fontSize:15,color:'#3B3A8F'}}>{MONTHS[month-1]} {year} — Production Summary</div>
                {monthly.weeks.length > 0 && (
                  <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:12,marginBottom:20}}>
                    {monthly.weeks.map((w,i) => {
                      const eff = w.efficiency;
                      return (
                        <div key={i} style={{background:'white',border:'1px solid #eee',borderRadius:8,padding:14}}>
                          <div style={{fontSize:11,color:'#888',marginBottom:4}}>Week {i+1} · {fmtDate(w.week_start)}</div>
                          <div style={{fontSize:13,fontWeight:700,color:'#3B3A8F'}}>{Number(w.planned_qty||w.week_target||0).toLocaleString('en-IN')} planned</div>
                          <div style={{fontSize:13,color:'#166534',fontWeight:700}}>{Number(w.actual_qty||w.week_actual||0).toLocaleString('en-IN')} actual</div>
                          <div style={{fontSize:12,color:effColor(eff),fontWeight:700}}>{eff||0}% eff</div>
                        </div>
                      );
                    })}
                  </div>
                )}
                <div style={{fontWeight:700,marginBottom:8,fontSize:13}}>Cutting — GPO wise</div>
                {monthly.cutting.length===0?<div style={{color:'#aaa',padding:20,textAlign:'center'}}>No cutting data for {MONTHS[month-1]} {year}</div>:(
                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:20}}>
                    <thead><tr style={{background:'#F7F6F3'}}>
                      {['GPO','Style','Buyer','Fabric','Order Qty','Month Target','Month Actual','Balance','Efficiency','Period'].map(h=>(
                        <th key={h} style={{padding:'8px 10px',textAlign:'left',color:'#555',fontWeight:600,borderBottom:'2px solid #eee',fontSize:11}}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {monthly.cutting.map((r,i) => {
                        const bal = (r.order_cut_qty||0) - (r.month_actual||0);
                        return (
                          <tr key={i} style={{borderBottom:'1px solid #f5f5f5'}}>
                            <td style={{padding:'8px 10px',fontFamily:'monospace',fontWeight:600,color:'#3B3A8F'}}>{r.gpo_no}</td>
                            <td style={{padding:'8px 10px'}}>{r.style}</td>
                            <td style={{padding:'8px 10px'}}>{r.buyer}</td>
                            <td style={{padding:'8px 10px',fontSize:11,color:'#666'}}>{r.fabric_type}</td>
                            <td style={{padding:'8px 10px',textAlign:'right'}}>{Number(r.order_cut_qty||0).toLocaleString('en-IN')}</td>
                            <td style={{padding:'8px 10px',textAlign:'right',color:'#3B3A8F',fontWeight:600}}>{Number(r.month_target||0).toLocaleString('en-IN')}</td>
                            <td style={{padding:'8px 10px',textAlign:'right',color:'#166534',fontWeight:600}}>{Number(r.month_actual||0).toLocaleString('en-IN')}</td>
                            <td style={{padding:'8px 10px',textAlign:'right',color:bal>0?'#ef4444':'#166534',fontWeight:600}}>{Number(bal).toLocaleString('en-IN')}</td>
                            <td style={{padding:'8px 10px',textAlign:'center'}}><span style={{padding:'2px 8px',borderRadius:10,fontSize:11,fontWeight:600,background:effBg(r.efficiency),color:effColor(r.efficiency)}}>{r.efficiency||0}%</span></td>
                            <td style={{padding:'8px 10px',fontSize:11,color:'#888'}}>{fmtDate(r.start_date)} — {fmtDate(r.end_date)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {view==='quarterly' && (
              <div>
                <div style={{marginBottom:16,fontWeight:700,fontSize:15,color:'#3B3A8F'}}>Q{quarter} {year} — Quarterly Plan</div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20}}>
                  <div>
                    <div style={{fontWeight:700,fontSize:13,marginBottom:10,color:'#333'}}>Buyer-wise Order Loading</div>
                    {quarterly.buyer_wise.length===0?<div style={{color:'#aaa',padding:20,textAlign:'center',border:'1px dashed #eee',borderRadius:8}}>No orders for Q{quarter} {year}</div>:(
                      <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                        <thead><tr style={{background:'#F7F6F3'}}>
                          {['Buyer','Month','Orders','Total Qty','Cut','Stitch'].map(h=>(
                            <th key={h} style={{padding:'7px 10px',textAlign:'left',color:'#555',fontWeight:600,borderBottom:'2px solid #eee',fontSize:11}}>{h}</th>
                          ))}
                        </tr></thead>
                        <tbody>
                          {quarterly.buyer_wise.map((r,i) => (
                            <tr key={i} style={{borderBottom:'1px solid #f5f5f5'}}>
                              <td style={{padding:'7px 10px',fontWeight:600}}>{r.buyer_name}</td>
                              <td style={{padding:'7px 10px'}}>{MONTHS[(r.month||1)-1]}</td>
                              <td style={{padding:'7px 10px',textAlign:'center'}}>{r.order_count}</td>
                              <td style={{padding:'7px 10px',textAlign:'right',fontWeight:600,color:'#3B3A8F'}}>{Number(r.total_qty||0).toLocaleString('en-IN')}</td>
                              <td style={{padding:'7px 10px',textAlign:'right',color:'#166534'}}>{Number(r.cut_actual||0).toLocaleString('en-IN')}</td>
                              <td style={{padding:'7px 10px',textAlign:'right',color:'#166534'}}>{Number(r.stitch_actual||0).toLocaleString('en-IN')}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                  <div>
                    <div style={{fontWeight:700,fontSize:13,marginBottom:10,color:'#333'}}>Capacity vs Loading (Weekly)</div>
                    {quarterly.capacity_wise.length===0?<div style={{color:'#aaa',padding:20,textAlign:'center',border:'1px dashed #eee',borderRadius:8}}>No capacity data for Q{quarter} {year}</div>:(
                      <div>
                        {quarterly.capacity_wise.map((w,i) => {
                          const pct = w.planned_qty>0?Math.min(100,Math.round(w.actual_qty/w.planned_qty*100)):0;
                          return (
                            <div key={i} style={{marginBottom:10,padding:'10px 14px',background:'#F7F6F3',borderRadius:6}}>
                              <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
                                <span style={{fontSize:12,fontWeight:600}}>{fmtDate(w.week_start)}</span>
                                <span style={{fontSize:12,color:'#888'}}>Lines: {w.lines_used}</span>
                                <span style={{fontSize:12,fontWeight:700,color:effColor(w.efficiency)}}>{w.efficiency||0}%</span>
                              </div>
                              <div style={{display:'flex',justifyContent:'space-between',fontSize:11,marginBottom:6}}>
                                <span style={{color:'#3B3A8F'}}>Plan: {Number(w.planned_qty||0).toLocaleString('en-IN')}</span>
                                <span style={{color:'#166534'}}>Actual: {Number(w.actual_qty||0).toLocaleString('en-IN')}</span>
                              </div>
                              <div style={{height:8,background:'#e5e7eb',borderRadius:4}}>
                                <div style={{height:'100%',width:pct+'%',background:effColor(w.efficiency),borderRadius:4,transition:'width 0.3s'}}/>
                              </div>
                            </div>
                          );
                        })}
                        {quarterly.monthly_summary.map((m,i) => (
                          <div key={i} style={{padding:'10px 14px',background:'#EEEDF8',borderRadius:6,marginBottom:8}}>
                            <div style={{display:'flex',justifyContent:'space-between',fontSize:13}}>
                              <b>{MONTHS[(m.month||1)-1]} {year}</b>
                              <span>T: {Number(m.target||0).toLocaleString('en-IN')} · A: {Number(m.actual||0).toLocaleString('en-IN')}</span>
                              <b style={{color:effColor(m.efficiency)}}>{m.efficiency||0}%</b>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {view==='downtime' && (
              <div>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                  <thead><tr style={{background:'#F7F6F3'}}>
                    {['Date','Dept','Line','Start','Stop','Duration','Reason','Remarks',''].map(h=>(
                      <th key={h} style={{padding:'8px 10px',textAlign:'left',color:'#555',fontWeight:600,borderBottom:'2px solid #eee',fontSize:11}}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {downtime.length===0?<tr><td colSpan={9} style={{padding:24,textAlign:'center',color:'#aaa'}}>No downtime logged this week</td></tr>:
                    downtime.map((r,i) => (
                      <tr key={i} style={{borderBottom:'1px solid #f5f5f5'}}>
                        <td style={{padding:'8px 10px'}}>{fmtDate(r.log_date)}</td>
                        <td style={{padding:'8px 10px',textTransform:'capitalize'}}>{r.dept}</td>
                        <td style={{padding:'8px 10px',textAlign:'center'}}>{r.line_no}</td>
                        <td style={{padding:'8px 10px'}}>{fmtTime(r.start_at)}</td>
                        <td style={{padding:'8px 10px'}}>{r.end_at ? fmtTime(r.end_at) : <span style={{color:'#9A6B00',fontWeight:600}}>open</span>}</td>
                        <td style={{padding:'8px 10px',fontWeight:700,color:'#ef4444'}}>{r.duration_mins!=null ? r.duration_mins+' mins' : '—'}</td>
                        <td style={{padding:'8px 10px'}}><span style={{padding:'2px 8px',borderRadius:10,fontSize:11,background:'#fee2e2',color:'#ef4444'}}>{r.reason_name}</span></td>
                        <td style={{padding:'8px 10px',color:'#888'}}>{r.remarks||'—'}</td>
                        <td style={{padding:'8px 10px'}}><button onClick={async()=>{try{await api.delete('/planning/downtime/'+r.id);}catch(e){reportError('delete this downtime entry')(e);}fetchDowntime(weekStart);}} style={{padding:'3px 8px',background:'#fee2e2',border:'none',borderRadius:4,color:'#ef4444',cursor:'pointer',fontSize:11}}>Delete</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <ListFooter shown={downtime.length} total={downtime.length} noun="downtime entries" />
              </div>
            )}
          </div>
        </div>
      </div>

      {showDowntimeModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.45)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
          <div style={{background:'white',borderRadius:12,padding:28,width:440}}>
            <div style={{ background: '#2D2C72', margin: '-28px -28px 18px', padding: '14px 24px', borderRadius: '12px 12px 0 0' }}><h3 style={{ margin: 0, fontSize: 16, color: '#fff', fontWeight: 700 }}>Log Downtime</h3></div>
            <form onSubmit={saveDowntime}>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                <div><label style={labelStyle}>Department</label>
                  <select style={inputStyle} value={dtForm.dept} onChange={e=>setDtForm(p=>({...p,dept:e.target.value}))}>
                    <option value="cutting">Cutting</option><option value="stitching">Stitching</option><option value="finishing">Finishing</option>
                  </select>
                </div>
                <div><label style={labelStyle}>Line *</label>
                  <select style={inputStyle} value={dtForm.line_id} onChange={e=>setDtForm(p=>({...p,line_id:e.target.value}))}>
                    <option value="">Select line…</option>
                    {dtLines.map(l=><option key={l.id} value={l.id}>{l.line_no}</option>)}
                  </select>
                </div>
                <div><label style={labelStyle}>Start Time *</label><input style={inputStyle} type="time" required value={dtForm.start_time} onChange={e=>setDtForm(p=>({...p,start_time:e.target.value}))}/></div>
                <div><label style={labelStyle}>Stop Time *</label><input style={inputStyle} type="time" required value={dtForm.stop_time} onChange={e=>setDtForm(p=>({...p,stop_time:e.target.value}))}/></div>
                <div style={{gridColumn:'1/-1'}}><label style={labelStyle}>Reason *</label>
                  <select style={inputStyle} value={dtForm.reason_id} onChange={e=>setDtForm(p=>({...p,reason_id:e.target.value}))}>
                    <option value="">Select reason…</option>
                    {dtReasons.filter(r=>LINE_REASON_CATS.includes(r.category)).map(r=><option key={r.id} value={r.id}>{r.name}{r.is_planned?' (planned)':''}</option>)}
                  </select>
                </div>
                <div style={{gridColumn:'1/-1'}}><label style={labelStyle}>Remarks</label><input style={inputStyle} placeholder="Details..." value={dtForm.remarks} onChange={e=>setDtForm(p=>({...p,remarks:e.target.value}))}/></div>
              </div>
              <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
                <button type="button" onClick={()=>setShowDowntimeModal(false)} style={{padding:'8px 18px',background:'#f0f2f8',border:'1px solid #ddd',borderRadius:6,fontSize:13,cursor:'pointer'}}>Cancel</button>
                <button type="submit" disabled={saving} style={{padding:'8px 18px',background:'#E22D2D',color:'white',border:'none',borderRadius:6,fontSize:13,cursor:'pointer',fontWeight:600}}>{saving?'Saving...':'Log Downtime'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default function CentreBoard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  return (
    <div style={{minHeight:'100vh',background:'transparent',fontFamily:'inherit'}}>
      
      <CentreBoardContent />
    </div>
  );
}
